import { useEffect, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '@/auth/msal';
import { Config } from '@/components/Config';
import { Dashboard } from '@/components/Dashboard';

const devMode = import.meta.env.VITE_DEV_MODE === 'true';

type View = 'dashboard' | 'config';

function readView(): View {
  return window.location.hash === '#/config' ? 'config' : 'dashboard';
}

export function App() {
  const isAuthed = useIsAuthenticated();
  const { instance } = useMsal();
  const [view, setView] = useState<View>(readView);

  useEffect(() => {
    const onHash = () => setView(readView());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const navigate = (v: View) => {
    window.location.hash = v === 'config' ? '#/config' : '';
  };

  if (devMode || isAuthed) {
    return view === 'config' ? (
      <Config onNavigate={navigate} />
    ) : (
      <Dashboard onNavigate={navigate} />
    );
  }

  return (
    <main className="grid min-h-screen place-items-center px-6 py-16">
      <div className="w-full max-w-xl">
        <div className="label">Copilot Usage</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight text-ink-50">
          Adopción de Microsoft 365 Copilot
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-ink-300">
          Panel interno del grupo Cuatroochenta para revisar el uso real de las licencias
          Copilot por filial: cuántas hay asignadas, quién las está aprovechando y quién no.
        </p>

        <ul className="mt-8 space-y-3 text-sm text-ink-300">
          {[
            { t: 'KPIs claros', d: 'Licencias asignadas, adopción 30d, sin uso — actualizados a diario.' },
            { t: 'Vista por app', d: 'Cuántos usuarios usan Copilot en Outlook, Teams, Word, Excel…' },
            { t: 'Detalle por persona', d: 'Última actividad por app, antigüedad de la licencia.' },
            { t: 'Acceso por filial', d: 'Cada manager solo ve los empleados de su(s) línea(s) de negocio.' },
          ].map((x) => (
            <li key={x.t} className="flex items-start gap-3">
              <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
              <div>
                <span className="text-ink-100">{x.t}.</span> <span className="text-ink-400">{x.d}</span>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-10 border-t border-ink-800 pt-8">
          <button
            type="button"
            onClick={() => void instance.loginRedirect(loginRequest)}
            className="flex w-full items-center justify-center gap-3 border border-ink-700 px-4 py-2.5 text-sm text-ink-100 transition-colors hover:bg-ink-900"
          >
            <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Iniciar sesión con Microsoft
          </button>
          <p className="mt-4 text-xs text-ink-500">
            Acceso restringido a cuentas del tenant <span className="font-mono text-ink-400">cuatroochenta.com</span>.
            Si no tienes acceso, contacta con el administrador del panel.
          </p>
        </div>
      </div>
    </main>
  );
}
